// src/hooks/usePRTimesNews.js
import { useState, useEffect } from 'react';

const RSS_URL = '/companyrdf.php?company_id=74558';

/**
 * 記事URLからOGP画像を取得する
 * @param {string} articleUrl - 記事のURL
 * @returns {Promise<string|null>} - 画像URL
 */
async function fetchOgImage(articleUrl) {
  try {
    // 記事URLからプロキシ経由で取得
    const proxyUrl = articleUrl.replace('https://prtimes.jp', '');
    const response = await fetch(proxyUrl);
    if (!response.ok) return null;

    const html = await response.text();
    // og:imageメタタグから画像URLを抽出
    const ogImageMatch = html.match(/<meta\s+property="og:image"\s+content="([^"]+)"/i) ||
                         html.match(/<meta\s+content="([^"]+)"\s+property="og:image"/i);

    if (ogImageMatch && ogImageMatch[1]) {
      // HTMLエンティティをデコード
      return ogImageMatch[1].replace(/&amp;/g, '&');
    }
    return null;
  } catch (err) {
    console.error('Failed to fetch OG image:', err);
    return null;
  }
}

/**
 * PR TimesのRSSフィードを取得するカスタムフック
 * @param {number} limit - 取得する記事数
 * @returns {Object} { news, loading, error }
 */
export function usePRTimesNews(limit = 5) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(RSS_URL);
        if (!response.ok) {
          throw new Error('RSSの取得に失敗しました');
        }

        const text = await response.text();
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, 'application/xml');

        // パースエラーチェック
        const parseError = xml.querySelector('parsererror');
        if (parseError) {
          throw new Error('XMLのパースに失敗しました');
        }

        const items = xml.querySelectorAll('item');
        const newsData = [];

        items.forEach((item, index) => {
          if (index >= limit) return;

          const title = item.querySelector('title')?.textContent || '';
          const link = item.querySelector('link')?.textContent || '';
          const pubDate = item.querySelector('date')?.textContent || '';

          newsData.push({
            title,
            link,
            image: null, // 後でOGP画像を取得
            pubDate,
          });
        });

        // 各記事のOGP画像を並列で取得
        const newsWithImages = await Promise.all(
          newsData.map(async (item) => {
            const image = await fetchOgImage(item.link);
            return { ...item, image };
          })
        );

        setNews(newsWithImages);
      } catch (err) {
        console.error('PR Times RSS fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [limit]);

  return { news, loading, error };
}

export default usePRTimesNews;
